import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { RequireAuth, useAuth } from './auth/AuthContext';
import { AuthProviders } from './providers/AppProviders';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { TeamPage } from './pages/TeamPage';
import { TeamPermissionsPage } from './pages/TeamPermissionsPage';
import { AppShell } from './components/AppShell';
import { ClientSharePage } from './pages/ClientSharePage';
import { ClientPortalPage } from './pages/ClientPortalPage';
import { useMe } from './hooks/useOps';
import type { Capability } from './api/types';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectWorkspacePage } from './pages/ProjectWorkspacePage';
import { QualificationPage } from './pages/QualificationPage';
import { ReportPage } from './pages/ReportPage';
import { TrackTypesPage } from './pages/TrackTypesPage';
import { AgentBrainPage } from './pages/AgentBrainPage';
import { AgentAccessPage } from './pages/AgentAccessPage';
import { AgentActivityPage } from './pages/AgentActivityPage';
import { AgentToolsPage } from './pages/AgentToolsPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { BudgetsPage } from './pages/BudgetsPage';
import { StatementsPage } from './pages/StatementsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { QuotationsPage } from './pages/QuotationsPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { TaxClosePage } from './pages/TaxClosePage';
import { TaxPrepPage } from './pages/TaxPrepPage';
import { CompliancePage } from './pages/CompliancePage';
import { PettyCashPage } from './pages/PettyCashPage';
import { PeoplePage } from './pages/PeoplePage';
import { LeavePage } from './pages/LeavePage';
import { PayrollPage } from './pages/PayrollPage';

/** Gate a route by a capability flag; users who lack it bounce to a page they can see. */
function RequireCap({ cap, children }: { cap: keyof Capability; children: ReactElement }) {
  const { data: me, isLoading } = useMe();
  if (isLoading) return null;
  return me?.capability[cap] ? children : <Navigate to="/projects" replace />;
}

/** Old project routes (hub/setup/tracks) redirect into the workspace tabs. */
function RedirectToWorkspace({ tab }: { tab?: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={tab ? `/projects/${id}/${tab}` : `/projects/${id}`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public client dashboard — no auth; the URL token is the credential. */}
      <Route path="/share/:token" element={<ClientSharePage />} />
      <Route path="*" element={<AuthedApp />} />
    </Routes>
  );
}

/** The internal app: local login + shell + every authenticated route. */
function AuthedApp() {
  return (
    <AuthProviders>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Self-service password reset — public, no auth. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Customer portal: authenticated but outside the internal shell. */}
        <Route
          path="/portal/:projectId?"
          element={
            <RequireAuth>
              <ClientPortalPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<ShellRoutes />} />
      </Routes>
    </AuthProviders>
  );
}

function ShellRoutes() {
  const { user } = useAuth();
  // Customer logins never see the internal shell — overview portal only.
  if (user?.role === 'client') return <Navigate to="/portal" replace />;
  return (
    <RequireAuth>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/projects" element={<RequireCap cap="canViewProjects"><ProjectsPage /></RequireCap>} />
          {/* Old routes → workspace tabs */}
          <Route path="/projects/:id/hub" element={<RedirectToWorkspace />} />
          <Route path="/projects/:id/setup" element={<RedirectToWorkspace tab="plan" />} />
          <Route path="/projects/:id/tracks/:trackId" element={<RedirectToWorkspace tab="progress" />} />
          <Route path="/projects/:id/qualifications/:checklistId" element={<QualificationPage />} />
          <Route path="/projects/:id/report" element={<ReportPage />} />
          <Route path="/projects/:id/:tab?" element={<ProjectWorkspacePage />} />
          <Route path="/team" element={<RequireCap cap="canManageTeam"><TeamPage /></RequireCap>} />
          <Route
            path="/team/:id/permissions"
            element={<RequireCap cap="canManageTeam"><TeamPermissionsPage /></RequireCap>}
          />
          <Route
            path="/settings/track-types"
            element={<RequireCap cap="canManageTrackTypes"><TrackTypesPage /></RequireCap>}
          />
          {/* Finance — the ops money stack, re-homed here (2026-07 epic) */}
          <Route path="/finance/quotations" element={<RequireCap cap="canViewQuotations"><QuotationsPage /></RequireCap>} />
          <Route path="/finance/invoices" element={<RequireCap cap="canViewInvoices"><InvoicesPage /></RequireCap>} />
          <Route path="/finance/expenses" element={<RequireCap cap="canViewExpenses"><ExpensesPage /></RequireCap>} />
          <Route path="/finance/budgets" element={<RequireCap cap="canSeeCash"><BudgetsPage /></RequireCap>} />
          <Route path="/finance/statements" element={<RequireCap cap="canSeeCash"><StatementsPage /></RequireCap>} />
          <Route path="/finance/approvals" element={<RequireCap cap="canApprove"><ApprovalsPage /></RequireCap>} />
          <Route path="/finance/petty-cash" element={<RequireCap cap="canSeeCash"><PettyCashPage /></RequireCap>} />
          <Route path="/finance/tax-close" element={<RequireCap cap="canSeeCash"><TaxClosePage /></RequireCap>} />
          <Route path="/finance/tax-prep" element={<RequireCap cap="canSeeCash"><TaxPrepPage /></RequireCap>} />
          <Route path="/finance/compliance" element={<RequireCap cap="canSeeCash"><CompliancePage /></RequireCap>} />
          {/* HR — people directory, leave, payroll export (finance epic P4) */}
          <Route path="/hr/people" element={<RequireCap cap="canManageHr"><PeoplePage /></RequireCap>} />
          <Route path="/hr/leave" element={<RequireCap cap="canManageHr"><LeavePage /></RequireCap>} />
          <Route path="/hr/payroll" element={<RequireCap cap="canRunPayroll"><PayrollPage /></RequireCap>} />
          {/* Agent stack — staff governance of the AI brain + MCP boundary */}
          <Route path="/agent/brain" element={<RequireCap cap="canManageBrain"><AgentBrainPage /></RequireCap>} />
          <Route path="/agent/access" element={<RequireCap cap="canManageAgents"><AgentAccessPage /></RequireCap>} />
          <Route path="/agent/activity" element={<RequireCap cap="canManageAgents"><AgentActivityPage /></RequireCap>} />
          <Route path="/agent/tools" element={<RequireCap cap="canManageAgents"><AgentToolsPage /></RequireCap>} />
          <Route path="*" element={<Navigate to="/projects" replace />} />
        </Routes>
      </AppShell>
    </RequireAuth>
  );
}
